/**
 * Media API — Upload and serve chat media via Backblaze B2 + Cloudflare Worker
 *
 * Flow for uploading chat media:
 *   1. getUploadUrl(conversationId, file) → { uploadUrl, objectKey }
 *   2. uploadFileToB2(uploadUrl, file)    → direct PUT to B2 presigned URL
 *   3. registerMedia(objectKey, ...)      → save metadata in DynamoDB
 *   4. getDownloadUrl(objectKey, ...)     → { url } — short-lived signed URL
 *
 * For profile avatars:
 *   1. getAvatarUploadUrl(file)           → { uploadUrl, objectKey, publicUrl }
 *   2. uploadFileToB2(uploadUrl, file)    → direct PUT to B2
 *   3. Use publicUrl in profile update
 */

import { apiClient } from "./client";
import { prepareAvatarUpload } from "../utils/image-compression";

export interface UploadUrlResponse {
  uploadUrl: string;
  objectKey: string;
  expiresIn: number;
}

export interface DownloadUrlResponse {
  url: string;
  expiresIn: number;
  expiresAt: string;
}

export interface AvatarUploadUrlResponse {
  uploadUrl: string;
  objectKey: string;
  publicUrl: string;
  expiresIn: number;
}

export interface RegisterMediaPayload {
  objectKey: string;
  conversationId: string;
  contentType: string;
  fileSize: number;
  fileName?: string;
  width?: number;
  height?: number;
  /** For video/audio — playback duration in milliseconds. */
  durationMs?: number;
  /** Optional pointer to a generated thumbnail variant (next-batch feature). */
  thumbnailKey?: string;
}

/**
 * Step 1a: Get a presigned upload URL for chat media.
 */
export async function getChatUploadUrl(
  conversationId: string,
  file: File
): Promise<UploadUrlResponse> {
  return apiClient.post<UploadUrlResponse>("/api/media/upload-url", {
    conversationId,
    contentType: file.type,
    fileSize: file.size,
    fileName: file.name,
  });
}

/**
 * Step 1b: Get a presigned upload URL for a profile avatar.
 */
export async function getAvatarUploadUrl(file: File): Promise<AvatarUploadUrlResponse> {
  return apiClient.post<AvatarUploadUrlResponse>("/api/media/avatar-upload-url", {
    contentType: file.type,
    fileSize: file.size,
  });
}

/**
 * Step 2: Upload the file directly to B2 using the presigned URL.
 * This is a raw fetch (no auth headers — the presigned URL encodes all auth).
 * Reports upload progress via the onProgress callback.
 */
interface UploadError extends Error {
  /** HTTP status, or 0 when the request never got a response at all. */
  status?: number;
}

/** One PUT attempt. Resolves on 2xx, rejects with a classified error. */
function putToB2(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      // B2 explains itself in an XML body; the first 200 chars are enough to
      // tell SignatureDoesNotMatch from AccessDenied from a 5xx.
      const detail = (xhr.responseText || "").slice(0, 200).replace(/\s+/g, " ").trim();
      const error: UploadError = new Error(
        `Storage rejected the upload (${xhr.status}${xhr.statusText ? " " + xhr.statusText : ""})${detail ? ": " + detail : ""}`
      );
      error.status = xhr.status;
      reject(error);
    };

    xhr.onerror = () => {
      // No status at all — a dropped connection, DNS/TLS failure, or a
      // blocked cross-origin request. The browser deliberately tells JS
      // nothing more than "it failed".
      const error: UploadError = new Error("Couldn't reach storage — check your connection");
      error.status = 0;
      reject(error);
    };

    xhr.ontimeout = () => {
      const error: UploadError = new Error("Upload timed out");
      error.status = 0;
      reject(error);
    };

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.timeout = 5 * 60 * 1000;
    xhr.send(file);
  });
}

/**
 * PUT the bytes to B2, retrying transport failures.
 *
 * A phone on mobile data drops connections routinely, and a single dropped
 * PUT used to surface as a flat "Upload failed" with nothing to act on. Only
 * retry what retrying can fix — no-response errors and 5xx. A 4xx means the
 * presigned URL itself is wrong (expired, bad signature, denied), which fails
 * identically however many times you try.
 */
export async function uploadFileToB2(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  const backoffMs = [0, 800, 2400];
  let lastError: unknown;

  for (let attempt = 0; attempt < backoffMs.length; attempt++) {
    if (backoffMs[attempt] > 0) {
      await new Promise((resolve) => setTimeout(resolve, backoffMs[attempt]));
      onProgress?.(0);
    }
    try {
      await putToB2(uploadUrl, file, onProgress);
      return;
    } catch (err) {
      lastError = err;
      const status = (err as UploadError).status ?? 0;
      console.warn(
        `[media] upload attempt ${attempt + 1}/${backoffMs.length} failed (status ${status}):`,
        (err as Error).message
      );
      if (status !== 0 && status < 500) break;
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Upload failed");
}

/**
 * Step 3: Register the uploaded file metadata in the backend.
 */
export async function registerMedia(payload: RegisterMediaPayload): Promise<{ objectKey: string }> {
  return apiClient.post<{ objectKey: string }>("/api/media/register", payload);
}

/**
 * Delete a media object (admin/cleanup path). Returns 409 if any message
 * still references the key — for normal flows just delete the message and
 * let cascade-delete handle it.
 */
export async function deleteMedia(objectKey: string, conversationId: string): Promise<{ deleted: string }> {
  return apiClient.post<{ deleted: string }>("/api/media/delete", { objectKey, conversationId });
}

/**
 * Step 4: Get a short-lived signed download URL for a private chat media file.
 * The URL is valid for ~10 minutes and is served through the Cloudflare Worker.
 */
export async function getMediaDownloadUrl(
  objectKey: string,
  conversationId: string
): Promise<DownloadUrlResponse> {
  return apiClient.post<DownloadUrlResponse>("/api/media/download-url", {
    objectKey,
    conversationId,
  });
}

/**
 * Full upload pipeline for chat media:
 *   get presigned URL → upload to B2 → register metadata → get download URL
 *
 * Returns the short-lived download URL to embed in the message.
 */
export async function uploadChatMedia(
  file: File,
  conversationId: string,
  options?: {
    width?: number;
    height?: number;
    durationMs?: number;
    onProgress?: (percent: number) => void;
  }
): Promise<{ url: string; objectKey: string }> {
  // 1. Get upload URL
  const { uploadUrl, objectKey } = await getChatUploadUrl(conversationId, file);

  // 2. Upload to B2
  await uploadFileToB2(uploadUrl, file, options?.onProgress);

  // 3. Register metadata
  await registerMedia({
    objectKey,
    conversationId,
    contentType: file.type,
    fileSize: file.size,
    fileName: file.name,
    width: options?.width,
    height: options?.height,
    durationMs: options?.durationMs,
  });

  // 4. Get download URL
  const { url } = await getMediaDownloadUrl(objectKey, conversationId);

  return { url, objectKey };
}

/**
 * React Native variant of {@link uploadChatMedia}. The browser pipeline is
 * built around `File` + `XMLHttpRequest.send(file)`, neither of which exists
 * for a `file://` uri from `expo-image-picker`. Here we read the local uri
 * into a Blob via `fetch()` (RN supports this) and PUT the bytes to the
 * presigned B2 URL, then register + resolve a signed download URL exactly
 * like the web path. Same 4-step contract, same backend endpoints.
 */
export interface NativeMediaAsset {
  uri: string;
  contentType: string;
  fileSize: number;
  fileName?: string;
  width?: number;
  height?: number;
  durationMs?: number;
}

export async function uploadChatMediaFromUri(
  asset: NativeMediaAsset,
  conversationId: string
): Promise<{ url: string; objectKey: string }> {
  // 1. Presigned upload URL.
  const { uploadUrl, objectKey } = await apiClient.post<UploadUrlResponse>(
    "/api/media/upload-url",
    {
      conversationId,
      contentType: asset.contentType,
      fileSize: asset.fileSize,
      fileName: asset.fileName,
    }
  );

  // 2. Read the local file into a Blob and PUT it to B2 (no auth header — the
  //    presigned URL encodes auth). RN's fetch can read a file:// uri.
  const blob = await (await fetch(asset.uri)).blob();
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": asset.contentType },
    body: blob,
  });
  if (!putRes.ok) {
    throw new Error(`B2 upload failed: ${putRes.status} ${putRes.statusText}`);
  }

  // 3. Register metadata.
  await registerMedia({
    objectKey,
    conversationId,
    contentType: asset.contentType,
    fileSize: asset.fileSize,
    fileName: asset.fileName,
    width: asset.width,
    height: asset.height,
    durationMs: asset.durationMs,
  });

  // 4. Signed download URL to embed in the message.
  const { url } = await getMediaDownloadUrl(objectKey, conversationId);
  return { url, objectKey };
}

/**
 * Upload an image/GIF for use inside a note. Reuses the avatar presign path
 * (which mints a permanent, public CDN URL via the media worker) but — unlike
 * `uploadAvatar` — does NOT compress to WebP, so animated GIFs stay animated
 * and full-resolution artwork is preserved. Returns the public URL to embed.
 */
export async function uploadNoteImage(
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  const { uploadUrl, publicUrl } = await getAvatarUploadUrl(file);
  await uploadFileToB2(uploadUrl, file, onProgress);
  return publicUrl;
}

/**
 * React Native variant of {@link uploadNoteImage} / avatar uploads — takes a
 * local `file://` uri (from expo-image-picker) instead of a browser `File`,
 * reads it into a Blob, and PUTs to the avatar/public presign path. Returns
 * the permanent public CDN URL (used for note cover images + inline images).
 */
export async function uploadNoteImageFromUri(asset: {
  uri: string;
  contentType: string;
  fileSize: number;
}): Promise<string> {
  const { uploadUrl, publicUrl } = await apiClient.post<AvatarUploadUrlResponse>(
    "/api/media/avatar-upload-url",
    { contentType: asset.contentType, fileSize: asset.fileSize }
  );
  const blob = await (await fetch(asset.uri)).blob();
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": asset.contentType },
    body: blob,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  return publicUrl;
}

/**
 * Full upload pipeline for avatar images.
 * Returns the public URL (no auth needed for avatars).
 */
export async function uploadAvatar(
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  // Compress to 512px WebP first — avatars are never rendered larger than
  // ~80px on-screen, so keeping the original 4-12MB phone JPEG is pure
  // storage waste.
  const prepared = await prepareAvatarUpload(file);
  const { uploadUrl, publicUrl } = await getAvatarUploadUrl(prepared.file);
  await uploadFileToB2(uploadUrl, prepared.file, onProgress);
  return publicUrl;
}

/** Max size for a profile song upload. Mirrors the backend cap. */
export const MAX_PROFILE_SONG_BYTES = 5 * 1024 * 1024;

const PROFILE_SONG_TYPES = new Set(["audio/mpeg", "audio/mp3"]);

/** Append a cache-busting `v=` param. The profile song uses a stable object
 *  key (so re-uploads overwrite in place), but the worker serves public objects
 *  with a 90-day immutable cache — the `v=` makes the new file's URL distinct. */
export function cacheBustedUrl(url: string, v: number): string {
  return `${url}${url.includes("?") ? "&" : "?"}v=${v}`;
}

/** Step 1: presigned URL for the profile song (stable per-user key). */
export async function getProfileSongUploadUrl(file: File): Promise<AvatarUploadUrlResponse> {
  return apiClient.post<AvatarUploadUrlResponse>("/api/media/avatar-upload-url", {
    contentType: file.type,
    fileSize: file.size,
    kind: "song",
  });
}

/**
 * Full upload pipeline for a profile song (one mp3, ≤5MB). Re-uploading
 * overwrites the previous object in place (stable key), so the old song is
 * replaced. Returns a cache-busted public URL to store in `theme.song.url`.
 */
export async function uploadProfileSong(
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  if (file.size > MAX_PROFILE_SONG_BYTES) {
    throw new Error("Song too large. Maximum size is 5MB");
  }
  if (!PROFILE_SONG_TYPES.has(file.type)) {
    throw new Error("Only mp3 files are supported");
  }
  const { uploadUrl, publicUrl } = await getProfileSongUploadUrl(file);
  await uploadFileToB2(uploadUrl, file, onProgress);
  return cacheBustedUrl(publicUrl, Date.now());
}

/** Max size for a Brief file attachment. Mirrors the backend cap. */
export const MAX_BRIEF_FILE_BYTES = 10 * 1024 * 1024;

export interface BriefUploadResult {
  url: string;
  name: string;
  contentType: string;
  size: number;
}

/** Step 1: presigned URL for a Brief attachment (broad allowlist, ≤10MB). */
export async function getBriefUploadUrl(file: File): Promise<AvatarUploadUrlResponse> {
  return apiClient.post<AvatarUploadUrlResponse>("/api/media/avatar-upload-url", {
    contentType: file.type,
    fileSize: file.size,
    kind: "brief",
  });
}

/**
 * Upload an arbitrary Brief attachment (checklist image/file sub-item). Returns
 * the public URL + file metadata for storing on the checklist sub-item. The
 * server enforces the content-type allowlist (no svg/html/executables).
 */
export async function uploadBriefFile(
  file: File,
  onProgress?: (percent: number) => void
): Promise<BriefUploadResult> {
  if (file.size > MAX_BRIEF_FILE_BYTES) {
    throw new Error("File too large. Maximum size is 10MB");
  }
  const { uploadUrl, publicUrl } = await getBriefUploadUrl(file);
  await uploadFileToB2(uploadUrl, file, onProgress);
  return { url: publicUrl, name: file.name, contentType: file.type, size: file.size };
}

/**
 * Resolve a media URL. If it's an objectKey (starts with "chat/" or "avatars/"),
 * it's not yet a URL — use this to get the signed download URL from the backend.
 * If it's already a full URL, return it as-is.
 *
 * This is used when rendering existing messages that store objectKeys.
 */
export function isObjectKey(value: string): boolean {
  return value.startsWith("chat/") || value.startsWith("avatars/");
}
